import { useState, useEffect, createContext, useContext } from "react";

// ===== CONFIG =====
const API_BASE = "https://amathematics.in/backend/api";
const ADMIN_API_BASE = "https://amathematics.in/backend/admin";

// ===== AUTH CONTEXT =====
const AuthContext = createContext(null);
const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("math_token"));
  useEffect(() => {
    if (token) {
      fetch(`${API_BASE}/auth.php?action=me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json()).then(d => { if (d.user) setUser(d.user); else { setToken(null); localStorage.removeItem("math_token"); } });
    }
  }, [token]);
  const login = (t, u) => { setToken(t); setUser(u); localStorage.setItem("math_token", t); };
  const logout = () => { setToken(null); setUser(null); localStorage.removeItem("math_token"); };
  return <AuthContext.Provider value={{ user, token, login, logout }}>{children}</AuthContext.Provider>;
}

// ===== MATH SYMBOLS =====
const MATH_SYMBOLS = [
  { cat: "Basic", syms: ["+", "-", "×", "÷", "=", "≠", "<", ">", "≤", "≥", "±", "∓", "%", "√", "∛", "∞"] },
  { cat: "Greek", syms: ["α", "β", "γ", "δ", "ε", "θ", "λ", "μ", "π", "σ", "τ", "φ", "ω", "Σ", "Δ", "Ω"] },
  { cat: "Calculus", syms: ["∫", "∬", "∮", "∂", "∇", "lim", "→", "Σ", "Π", "d/dx", "dy/dx"] },
  { cat: "Sets", syms: ["∈", "∉", "⊂", "⊃", "⊆", "⊇", "∪", "∩", "∅", "ℕ", "ℤ", "ℚ", "ℝ", "ℂ"] },
  { cat: "Logic", syms: ["∀", "∃", "∄", "∧", "∨", "¬", "⇒", "⇔", "⊕", "⊗"] },
  { cat: "Fractions", syms: ["½", "⅓", "⅔", "¼", "¾", "⅛", "⅜", "⅝", "⅞"] },
  { cat: "Powers", syms: ["x²", "x³", "xⁿ", "x⁻¹", "x½", "log", "ln", "eˣ"] },
  { cat: "Trig", syms: ["sin", "cos", "tan", "cot", "sec", "csc", "sin⁻¹", "cos⁻¹", "tan⁻¹"] },
];

// ===== MATH SOLVER TOOL =====
function MathSymbolBar({ onInsert }) {
  const [activeCat, setActiveCat] = useState("Basic");
  const active = MATH_SYMBOLS.find(c => c.cat === activeCat);
  return (
    <div style={{ background: "#0a1628", borderRadius: "10px", padding: "10px", marginBottom: "10px" }}>
      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "8px" }}>
        {MATH_SYMBOLS.map(c => (
          <button key={c.cat} onClick={() => setActiveCat(c.cat)}
            style={{ padding: "3px 10px", borderRadius: "20px", border: "none", cursor: "pointer", fontSize: "12px", background: activeCat === c.cat ? "#ff6b35" : "#1a2744", color: "#fff", transition: "all .2s" }}>
            {c.cat}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
        {active?.syms.map(s => (
          <button key={s} onClick={() => onInsert(s)}
            style={{ padding: "4px 8px", background: "#1a2744", border: "1px solid #2a3a64", color: "#e0e8ff", borderRadius: "6px", cursor: "pointer", fontFamily: "math, serif", fontSize: "14px", transition: "all .2s" }}
            onMouseEnter={e => { e.target.style.background = "#ff6b35"; e.target.style.borderColor = "#ff6b35"; }}
            onMouseLeave={e => { e.target.style.background = "#1a2744"; e.target.style.borderColor = "#2a3a64"; }}>
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function ChatMessage({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", marginBottom: "16px", animation: "fadeIn .3s ease" }}>
      {!isUser && (
        <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "linear-gradient(135deg,#ff6b35,#f7c59f)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginRight: "10px", fontSize: "16px" }}>🧮</div>
      )}
      <div style={{
        maxWidth: "75%", padding: "12px 16px", borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
        background: isUser ? "linear-gradient(135deg,#ff6b35,#e05520)" : "#1a2744",
        color: "#e0e8ff", fontSize: "14px", lineHeight: "1.7", whiteSpace: "pre-wrap",
        boxShadow: "0 2px 12px rgba(0,0,0,.3)", border: isUser ? "none" : "1px solid #2a3a64"
      }}>
        {msg.content}
      </div>
      {isUser && (
        <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "#2a3a64", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginLeft: "10px", fontSize: "16px" }}>👤</div>
      )}
    </div>
  );
}

function SolverPage() {
  const { user, token } = useAuth();
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm your AI Math Tutor. Ask me any math problem — algebra, calculus, geometry, statistics, and more. I'll solve it step-by-step! 🧮" }
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [limitReached, setLimitReached] = useState(false);
  const textareaRef = useState(null);

  const insertSymbol = (sym) => {
    setInput(prev => prev + sym);
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const question = input.trim();
    setInput("");
    setError("");
    const history = messages.filter(m => m.role !== "assistant" || messages.indexOf(m) > 0).slice(-8);
    setMessages(prev => [...prev, { role: "user", content: question }]);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/solve.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ question, history }),
      });
      const data = await res.json();
      if (data.error) {
        if (data.limit_reached) setLimitReached(true);
        setError(data.error);
      } else {
        setMessages(prev => [...prev, { role: "assistant", content: data.answer }]);
      }
    } catch { setError("Connection error. Please try again."); }
    setLoading(false);
  };

  const clearChat = () => {
    setMessages([{ role: "assistant", content: "Chat cleared! Ask me a new math problem. 🧮" }]);
    setError(""); setLimitReached(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg,#060d1f 0%,#0d1b3e 50%,#061020 100%)", color: "#e0e8ff", fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}} @keyframes spin{to{transform:rotate(360deg)}} * {box-sizing:border-box} ::-webkit-scrollbar{width:6px} ::-webkit-scrollbar-track{background:#0a1628} ::-webkit-scrollbar-thumb{background:#2a3a64;border-radius:3px}`}</style>

      {/* HEADER */}
      <header style={{ background: "rgba(10,22,40,.95)", backdropFilter: "blur(20px)", borderBottom: "1px solid #1a2a4a", padding: "0 24px", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: "64px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer" }} onClick={() => window.location.hash = "#home"}>
            <div style={{ width: "38px", height: "38px", background: "linear-gradient(135deg,#ff6b35,#e05520)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px" }}>∑</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: "18px", color: "#fff", letterSpacing: "-.3px" }}>MathAI</div>
              <div style={{ fontSize: "10px", color: "#ff6b35", letterSpacing: "1px", textTransform: "uppercase" }}>Tutor</div>
            </div>
          </div>
          <nav style={{ display: "flex", gap: "24px", alignItems: "center" }}>
            {["Home", "Solver", "Pricing", "About"].map(n => (
              <a key={n} href={`#${n.toLowerCase()}`} style={{ color: "#a0b0d0", textDecoration: "none", fontSize: "14px", transition: "color .2s" }}
                onMouseEnter={e => e.target.style.color = "#ff6b35"} onMouseLeave={e => e.target.style.color = "#a0b0d0"}>{n}</a>
            ))}
            {user ? (
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "13px", color: "#ff6b35" }}>{user.plan?.toUpperCase()}</span>
                <div style={{ padding: "6px 14px", background: "#1a2744", borderRadius: "8px", fontSize: "13px", color: "#fff" }}>{user.name?.split(" ")[0]}</div>
              </div>
            ) : (
              <div style={{ display: "flex", gap: "8px" }}>
                <button onClick={() => window.location.hash = "#login"} style={{ padding: "7px 16px", background: "transparent", border: "1px solid #2a3a64", color: "#e0e8ff", borderRadius: "8px", cursor: "pointer", fontSize: "13px" }}>Login</button>
                <button onClick={() => window.location.hash = "#pricing"} style={{ padding: "7px 16px", background: "linear-gradient(135deg,#ff6b35,#e05520)", border: "none", color: "#fff", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>Get Started</button>
              </div>
            )}
          </nav>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <main style={{ maxWidth: "1100px", margin: "0 auto", padding: "32px 24px" }}>
        {/* Hero Section */}
        <div style={{ textAlign: "center", marginBottom: "40px", animation: "fadeIn .6s ease" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "rgba(255,107,53,.1)", border: "1px solid rgba(255,107,53,.3)", borderRadius: "20px", padding: "6px 16px", marginBottom: "20px" }}>
            <span style={{ color: "#ff6b35", fontSize: "12px", fontWeight: 600, letterSpacing: ".5px" }}>✦ AI-POWERED MATHEMATICS TUTOR</span>
          </div>
          <h1 style={{ fontSize: "clamp(28px,5vw,52px)", fontWeight: 800, margin: "0 0 16px", lineHeight: 1.1, background: "linear-gradient(135deg,#fff 0%,#a0b8ff 50%,#ff6b35 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Solve Any Math Problem<br />Instantly with AI
          </h1>
          <p style={{ color: "#6a80a0", fontSize: "16px", maxWidth: "580px", margin: "0 auto 32px" }}>
            From basic algebra to advanced calculus — get step-by-step solutions with clear explanations.
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: "24px", flexWrap: "wrap" }}>
            {[["∞", "Topics Covered"], ["98%", "Accuracy"], ["24/7", "Available"], ["Free", "To Start"]].map(([v, l]) => (
              <div key={l} style={{ textAlign: "center" }}>
                <div style={{ fontSize: "22px", fontWeight: 800, color: "#ff6b35" }}>{v}</div>
                <div style={{ fontSize: "12px", color: "#6a80a0" }}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* SOLVER BOX */}
        <div style={{ background: "rgba(13,27,62,.6)", border: "1px solid #1a2a4a", borderRadius: "20px", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,.5)" }}>
          {/* Chat Area */}
          <div style={{ height: "420px", overflowY: "auto", padding: "24px" }}>
            {messages.map((m, i) => <ChatMessage key={i} msg={m} />)}
            {loading && (
              <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "16px" }}>
                <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "linear-gradient(135deg,#ff6b35,#f7c59f)", display: "flex", alignItems: "center", justifyContent: "center" }}>🧮</div>
                <div style={{ background: "#1a2744", border: "1px solid #2a3a64", borderRadius: "18px", padding: "12px 20px", display: "flex", gap: "6px" }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#ff6b35", animation: "bounce 1.2s infinite", animationDelay: `${i * 0.2}s` }} />
                  ))}
                </div>
              </div>
            )}
            {error && (
              <div style={{ background: "rgba(255,60,60,.1)", border: "1px solid rgba(255,60,60,.3)", borderRadius: "10px", padding: "12px 16px", color: "#ff8080", fontSize: "14px", marginBottom: "12px" }}>
                ⚠️ {error}
                {limitReached && <button onClick={() => window.location.hash = "#pricing"} style={{ marginLeft: "12px", padding: "4px 12px", background: "#ff6b35", border: "none", color: "#fff", borderRadius: "6px", cursor: "pointer", fontSize: "12px" }}>Upgrade Plan</button>}
              </div>
            )}
          </div>

          {/* Input Area */}
          <div style={{ padding: "16px 24px 24px", borderTop: "1px solid #1a2a4a", background: "rgba(6,13,31,.5)" }}>
            <MathSymbolBar onInsert={insertSymbol} />
            <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
              <div style={{ flex: 1, position: "relative" }}>
                <textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder="Type a math problem... (e.g. 'Solve: x² + 5x + 6 = 0' or 'Find the derivative of sin(x)cos(x)')"
                  rows={3}
                  style={{ width: "100%", background: "#0a1628", border: "1px solid #2a3a64", borderRadius: "12px", padding: "12px 16px", color: "#e0e8ff", fontSize: "14px", resize: "none", outline: "none", fontFamily: "inherit", lineHeight: "1.5" }}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <button onClick={sendMessage} disabled={!input.trim() || loading}
                  style={{ width: "50px", height: "50px", background: input.trim() && !loading ? "linear-gradient(135deg,#ff6b35,#e05520)" : "#1a2744", border: "none", borderRadius: "12px", cursor: input.trim() && !loading ? "pointer" : "not-allowed", fontSize: "20px", transition: "all .2s" }}>
                  {loading ? <span style={{ display: "inline-block", animation: "spin 1s linear infinite" }}>⟳</span> : "↑"}
                </button>
                <button onClick={clearChat} title="Clear chat"
                  style={{ width: "50px", height: "50px", background: "#1a2744", border: "1px solid #2a3a64", borderRadius: "12px", cursor: "pointer", fontSize: "16px", color: "#6a80a0" }}>
                  🗑
                </button>
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px", marginTop: "10px", flexWrap: "wrap" }}>
              {["Solve: x² - 5x + 6 = 0", "Find ∫x²dx", "Derivative of sin(x)cos(x)", "Area of circle r=5", "Factor: x³ - 8"].map(ex => (
                <button key={ex} onClick={() => setInput(ex)}
                  style={{ padding: "4px 12px", background: "#0a1628", border: "1px solid #2a3a64", borderRadius: "20px", color: "#6a80a0", fontSize: "12px", cursor: "pointer", transition: "all .2s" }}
                  onMouseEnter={e => { e.target.style.borderColor = "#ff6b35"; e.target.style.color = "#ff6b35"; }}
                  onMouseLeave={e => { e.target.style.borderColor = "#2a3a64"; e.target.style.color = "#6a80a0"; }}>
                  {ex}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* TOPICS SECTION */}
        <div style={{ marginTop: "64px", textAlign: "center" }}>
          <h2 style={{ fontSize: "32px", fontWeight: 700, color: "#fff", marginBottom: "8px" }}>Topics Covered</h2>
          <p style={{ color: "#6a80a0", marginBottom: "40px" }}>From basic to advanced — we cover it all</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: "16px" }}>
            {[["➕", "Arithmetic", "Basic operations"], ["📐", "Geometry", "Shapes & areas"], ["🔢", "Algebra", "Equations & functions"], ["📈", "Calculus", "Derivatives & integrals"], ["📊", "Statistics", "Data & probability"], ["🔺", "Trigonometry", "Sin, cos, tan"], ["🧩", "Linear Algebra", "Matrices & vectors"], ["∞", "Discrete Math", "Logic & sets"]].map(([icon, title, desc]) => (
              <div key={title} style={{ background: "#0d1b3e", border: "1px solid #1a2a4a", borderRadius: "16px", padding: "24px 16px", transition: "all .2s", cursor: "pointer" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "#ff6b35"; e.currentTarget.style.transform = "translateY(-4px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "#1a2a4a"; e.currentTarget.style.transform = "translateY(0)"; }}>
                <div style={{ fontSize: "28px", marginBottom: "10px" }}>{icon}</div>
                <div style={{ fontWeight: 600, color: "#fff", marginBottom: "4px" }}>{title}</div>
                <div style={{ fontSize: "12px", color: "#6a80a0" }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* HOW IT WORKS */}
        <div style={{ marginTop: "80px", textAlign: "center" }}>
          <h2 style={{ fontSize: "32px", fontWeight: 700, color: "#fff", marginBottom: "8px" }}>How It Works</h2>
          <p style={{ color: "#6a80a0", marginBottom: "48px" }}>Three simple steps to solve any math problem</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: "24px" }}>
            {[
              { step: "01", icon: "✏️", title: "Type Your Problem", desc: "Enter any math problem using the keyboard or click math symbols from the symbol bar above." },
              { step: "02", icon: "🤖", title: "AI Processes It", desc: "Our GPT-4 powered engine analyzes your problem and identifies the best solution method." },
              { step: "03", icon: "📋", title: "Get Step-by-Step Solution", desc: "Receive a detailed, easy-to-understand solution with explanations for every step." },
            ].map(({ step, icon, title, desc }) => (
              <div key={step} style={{ background: "rgba(13,27,62,.6)", border: "1px solid #1a2a4a", borderRadius: "20px", padding: "32px 24px", position: "relative", textAlign: "left" }}>
                <div style={{ position: "absolute", top: "20px", right: "20px", fontSize: "36px", fontWeight: 900, color: "rgba(255,107,53,.15)", fontFamily: "monospace" }}>{step}</div>
                <div style={{ fontSize: "36px", marginBottom: "16px" }}>{icon}</div>
                <div style={{ fontWeight: 700, color: "#fff", fontSize: "16px", marginBottom: "10px" }}>{title}</div>
                <div style={{ color: "#6a80a0", fontSize: "14px", lineHeight: "1.6" }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* TESTIMONIALS */}
        <div style={{ marginTop: "80px", textAlign: "center" }}>
          <h2 style={{ fontSize: "32px", fontWeight: 700, color: "#fff", marginBottom: "8px" }}>Loved by Students Worldwide</h2>
          <p style={{ color: "#6a80a0", marginBottom: "48px" }}>Join thousands of students acing their math</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: "20px" }}>
            {[
              { name: "Priya Sharma", role: "Class 12 Student", text: "MathAI explained integration by parts better than my textbook. Got 95% in my boards!", avatar: "🧕" },
              { name: "Rahul Verma", role: "Engineering Student", text: "Solved my differential equations assignment in minutes. The steps are crystal clear.", avatar: "👨‍💻" },
              { name: "Anita Patel", role: "MBA Aspirant", text: "Statistics and probability questions that used to take hours now take seconds. Amazing!", avatar: "👩‍🎓" },
            ].map(({ name, role, text, avatar }) => (
              <div key={name} style={{ background: "rgba(13,27,62,.6)", border: "1px solid #1a2a4a", borderRadius: "16px", padding: "24px", textAlign: "left" }}>
                <div style={{ color: "#ff6b35", fontSize: "20px", marginBottom: "12px" }}>★★★★★</div>
                <p style={{ color: "#a0b8d0", fontSize: "14px", lineHeight: "1.7", margin: "0 0 16px", fontStyle: "italic" }}>"{text}"</p>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "#1a2744", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px" }}>{avatar}</div>
                  <div>
                    <div style={{ color: "#fff", fontWeight: 600, fontSize: "14px" }}>{name}</div>
                    <div style={{ color: "#6a80a0", fontSize: "12px" }}>{role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA BANNER */}
        <div style={{ marginTop: "80px", background: "linear-gradient(135deg,rgba(255,107,53,.15),rgba(255,107,53,.05))", border: "1px solid rgba(255,107,53,.3)", borderRadius: "24px", padding: "60px 40px", textAlign: "center" }}>
          <h2 style={{ fontSize: "36px", fontWeight: 800, color: "#fff", margin: "0 0 12px" }}>Ready to Master Mathematics?</h2>
          <p style={{ color: "#a0b8d0", fontSize: "16px", margin: "0 0 32px" }}>Start solving for free. No credit card required.</p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              style={{ padding: "14px 36px", background: "linear-gradient(135deg,#ff6b35,#e05520)", border: "none", borderRadius: "12px", color: "#fff", fontWeight: 700, fontSize: "16px", cursor: "pointer" }}>
              Start Solving Free →
            </button>
            <button onClick={() => window.location.hash = "#pricing"}
              style={{ padding: "14px 36px", background: "transparent", border: "1px solid #2a3a64", borderRadius: "12px", color: "#a0b8d0", fontWeight: 600, fontSize: "16px", cursor: "pointer" }}>
              View Pricing
            </button>
          </div>
        </div>
      </main>

      {/* ===== FOOTER ===== */}
      <footer style={{ background: "#030810", borderTop: "1px solid #1a2a4a", marginTop: "80px" }}>
        {/* Top footer */}
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "60px 24px 40px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: "48px", flexWrap: "wrap" }}>

            {/* Brand */}
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
                <div style={{ width: "38px", height: "38px", background: "linear-gradient(135deg,#ff6b35,#e05520)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", fontWeight: 800, color: "#fff" }}>∑</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "18px", color: "#fff" }}>MathAI Tutor</div>
                  <div style={{ fontSize: "10px", color: "#ff6b35", letterSpacing: "1px" }}>AI-POWERED LEARNING</div>
                </div>
              </div>
              <p style={{ color: "#6a80a0", fontSize: "14px", lineHeight: "1.7", margin: "0 0 20px", maxWidth: "280px" }}>
                Your 24/7 AI mathematics tutor. From basic arithmetic to advanced calculus — we make math easy with step-by-step solutions.
              </p>
              <div style={{ display: "flex", gap: "10px" }}>
                {[
                  { icon: "𝕏", label: "Twitter" },
                  { icon: "in", label: "LinkedIn" },
                  { icon: "f", label: "Facebook" },
                  { icon: "▶", label: "YouTube" },
                ].map(s => (
                  <a key={s.label} href="#" title={s.label}
                    style={{ width: "36px", height: "36px", background: "#0d1b3e", border: "1px solid #2a3a64", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", color: "#6a80a0", textDecoration: "none", fontSize: "13px", fontWeight: 700, transition: "all .2s" }}
                    onMouseEnter={e => { e.currentTarget.style.background = "#ff6b35"; e.currentTarget.style.borderColor = "#ff6b35"; e.currentTarget.style.color = "#fff"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "#0d1b3e"; e.currentTarget.style.borderColor = "#2a3a64"; e.currentTarget.style.color = "#6a80a0"; }}>
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Product */}
            <div>
              <h4 style={{ color: "#fff", fontWeight: 700, fontSize: "14px", marginBottom: "16px", letterSpacing: ".5px", textTransform: "uppercase" }}>Product</h4>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {[["Math Solver", "#home"], ["Pricing", "#pricing"], ["Features", "#home"], ["API Access", "#pricing"], ["Mobile App", "#"]].map(([label, href]) => (
                  <li key={label} style={{ marginBottom: "10px" }}>
                    <a href={href} style={{ color: "#6a80a0", textDecoration: "none", fontSize: "14px", transition: "color .2s" }}
                      onMouseEnter={e => e.target.style.color = "#ff6b35"}
                      onMouseLeave={e => e.target.style.color = "#6a80a0"}>{label}</a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Topics */}
            <div>
              <h4 style={{ color: "#fff", fontWeight: 700, fontSize: "14px", marginBottom: "16px", letterSpacing: ".5px", textTransform: "uppercase" }}>Topics</h4>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {[["Algebra", "#"], ["Calculus", "#"], ["Geometry", "#"], ["Statistics", "#"], ["Trigonometry", "#"], ["Linear Algebra", "#"]].map(([label, href]) => (
                  <li key={label} style={{ marginBottom: "10px" }}>
                    <a href={href} style={{ color: "#6a80a0", textDecoration: "none", fontSize: "14px", transition: "color .2s" }}
                      onMouseEnter={e => e.target.style.color = "#ff6b35"}
                      onMouseLeave={e => e.target.style.color = "#6a80a0"}>{label}</a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 style={{ color: "#fff", fontWeight: 700, fontSize: "14px", marginBottom: "16px", letterSpacing: ".5px", textTransform: "uppercase" }}>Company</h4>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {[["About Us", "#"], ["Contact", "#"], ["Privacy Policy", "#"], ["Terms of Service", "#"], ["Refund Policy", "#"], ["Sitemap", "#"]].map(([label, href]) => (
                  <li key={label} style={{ marginBottom: "10px" }}>
                    <a href={href} style={{ color: "#6a80a0", textDecoration: "none", fontSize: "14px", transition: "color .2s" }}
                      onMouseEnter={e => e.target.style.color = "#ff6b35"}
                      onMouseLeave={e => e.target.style.color = "#6a80a0"}>{label}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Newsletter */}
          <div style={{ marginTop: "48px", padding: "32px", background: "#0d1b3e", border: "1px solid #1a2a4a", borderRadius: "16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "24px", flexWrap: "wrap" }}>
            <div>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: "16px", marginBottom: "4px" }}>📬 Get Math Tips in Your Inbox</div>
              <div style={{ color: "#6a80a0", fontSize: "13px" }}>Weekly tips, tricks and solved problems delivered free.</div>
            </div>
            <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
              <input placeholder="Enter your email" type="email"
                style={{ padding: "10px 16px", background: "#0a1628", border: "1px solid #2a3a64", borderRadius: "10px", color: "#e0e8ff", fontSize: "14px", outline: "none", width: "220px" }} />
              <button style={{ padding: "10px 20px", background: "linear-gradient(135deg,#ff6b35,#e05520)", border: "none", borderRadius: "10px", color: "#fff", fontWeight: 600, cursor: "pointer", fontSize: "14px", whiteSpace: "nowrap" }}>
                Subscribe
              </button>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{ borderTop: "1px solid #1a2a4a" }}>
          <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
            <div style={{ color: "#4a5a7a", fontSize: "13px" }}>
              © {new Date().getFullYear()} MathAI Tutor. All rights reserved. Built with ❤️ for students everywhere.
            </div>
            <div style={{ display: "flex", gap: "20px" }}>
              {["Privacy Policy", "Terms of Service", "Cookie Policy"].map(l => (
                <a key={l} href="#" style={{ color: "#4a5a7a", fontSize: "13px", textDecoration: "none", transition: "color .2s" }}
                  onMouseEnter={e => e.target.style.color = "#ff6b35"}
                  onMouseLeave={e => e.target.style.color = "#4a5a7a"}>{l}</a>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#4a5a7a", fontSize: "12px" }}>
              <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#10b981", display: "inline-block" }}></span>
              All systems operational
            </div>
          </div>
        </div>
      </footer>

      <style>{`@keyframes bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}`}</style>
    </div>
  );
}

// ===== PRICING PAGE =====
function PricingPage() {
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState("monthly");
  const [gateways, setGateways] = useState({});
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  useEffect(() => {
    fetch(`${API_BASE}/plans.php`).then(r => r.json()).then(d => {
      setPlans(d.plans || []); setGateways(d.gateways || {}); setLoading(false);
    });
  }, []);

  const handleSubscribe = async (plan) => {
    if (!token) { window.location.hash = "#login"; return; }
    if (plan.slug === "free") return;
    if (gateways.stripe) {
      const res = await fetch(`${API_BASE}/payment.php?action=stripe_checkout`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan_id: plan.id, cycle: billing }),
      });
      const d = await res.json();
      if (d.url) window.location.href = d.url;
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg,#060d1f,#0d1b3e,#061020)", color: "#e0e8ff", fontFamily: "'Segoe UI',system-ui,sans-serif", padding: "80px 24px 60px" }}>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "48px", animation: "fadeIn .5s ease" }}>
          <h1 style={{ fontSize: "44px", fontWeight: 800, background: "linear-gradient(135deg,#fff,#a0b8ff,#ff6b35)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", margin: "0 0 16px" }}>Simple, Transparent Pricing</h1>
          <p style={{ color: "#6a80a0", fontSize: "16px" }}>Choose a plan that fits your needs</p>
          <div style={{ display: "inline-flex", background: "#0d1b3e", border: "1px solid #2a3a64", borderRadius: "40px", padding: "4px", marginTop: "24px" }}>
            {["monthly", "yearly"].map(b => (
              <button key={b} onClick={() => setBilling(b)}
                style={{ padding: "8px 24px", borderRadius: "36px", border: "none", cursor: "pointer", fontSize: "14px", fontWeight: 600, background: billing === b ? "#ff6b35" : "transparent", color: billing === b ? "#fff" : "#6a80a0", transition: "all .2s" }}>
                {b === "monthly" ? "Monthly" : "Yearly (Save 20%)"}
              </button>
            ))}
          </div>
        </div>

        {loading ? <div style={{ textAlign: "center", color: "#6a80a0" }}>Loading plans...</div> : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: "24px" }}>
            {plans.map((plan, i) => (
              <div key={plan.id} style={{ background: plan.is_popular ? "linear-gradient(135deg,#1a2744,#0d1b3e)" : "#0d1b3e", border: plan.is_popular ? "2px solid #ff6b35" : "1px solid #1a2a4a", borderRadius: "20px", padding: "32px 24px", position: "relative", animation: `fadeIn ${.3 + i * .1}s ease`, transition: "transform .2s" }}
                onMouseEnter={e => e.currentTarget.style.transform = "translateY(-6px)"}
                onMouseLeave={e => e.currentTarget.style.transform = "translateY(0)"}>
                {plan.is_popular && <div style={{ position: "absolute", top: "-12px", left: "50%", transform: "translateX(-50%)", background: "#ff6b35", color: "#fff", padding: "4px 16px", borderRadius: "20px", fontSize: "12px", fontWeight: 700 }}>MOST POPULAR</div>}
                <div style={{ fontSize: "18px", fontWeight: 700, color: "#fff", marginBottom: "8px" }}>{plan.name}</div>
                <div style={{ marginBottom: "24px" }}>
                  <span style={{ fontSize: "40px", fontWeight: 800, color: plan.is_popular ? "#ff6b35" : "#fff" }}>
                    ${billing === "yearly" ? plan.price_yearly : plan.price_monthly}
                  </span>
                  {plan.price_monthly > 0 && <span style={{ color: "#6a80a0", fontSize: "14px" }}>/{billing === "yearly" ? "yr" : "mo"}</span>}
                </div>
                <div style={{ color: "#6a80a0", fontSize: "13px", marginBottom: "24px" }}>{plan.description}</div>
                <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px" }}>
                  {plan.features?.map(f => (
                    <li key={f} style={{ display: "flex", alignItems: "center", gap: "8px", color: "#a0b0d0", fontSize: "14px", marginBottom: "8px" }}>
                      <span style={{ color: "#ff6b35", fontWeight: 700 }}>✓</span> {f}
                    </li>
                  ))}
                </ul>
                <button onClick={() => handleSubscribe(plan)}
                  style={{ width: "100%", padding: "12px", background: plan.is_popular ? "linear-gradient(135deg,#ff6b35,#e05520)" : "#1a2744", border: plan.is_popular ? "none" : "1px solid #2a3a64", color: "#fff", borderRadius: "12px", cursor: "pointer", fontWeight: 600, fontSize: "15px", transition: "all .2s" }}>
                  {plan.slug === "free" ? "Get Started Free" : `Subscribe ${plan.name}`}
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "48px", color: "#6a80a0", fontSize: "13px" }}>
          Payments powered by
          {gateways.stripe && <span style={{ margin: "0 8px", color: "#a0b0d0" }}>💳 Stripe</span>}
          {gateways.paypal && <span style={{ margin: "0 8px", color: "#a0b0d0" }}>🅿 PayPal</span>}
          {gateways.razorpay && <span style={{ margin: "0 8px", color: "#a0b0d0" }}>₹ Razorpay</span>}
        </div>

        {/* FAQ */}
        <div style={{ marginTop: "72px" }}>
          <h2 style={{ fontSize: "32px", fontWeight: 700, color: "#fff", textAlign: "center", marginBottom: "8px" }}>Frequently Asked Questions</h2>
          <p style={{ color: "#6a80a0", textAlign: "center", marginBottom: "40px" }}>Everything you need to know about MathAI</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            {[
              ["Is the free plan really free?", "Yes! No credit card needed. You get 10 queries per month for free, forever."],
              ["Can I cancel anytime?", "Absolutely. Cancel your subscription anytime from your account dashboard with no penalty."],
              ["Which payment methods are accepted?", "We accept all major credit/debit cards via Stripe, PayPal, and Razorpay for Indian users."],
              ["What math topics are supported?", "Everything — from basic arithmetic to advanced calculus, linear algebra, statistics, and discrete math."],
              ["Is there a student discount?", "Yes! Yearly plans already save 20%. Contact us with your student ID for additional discounts."],
              ["Can I use MathAI on mobile?", "Yes, MathAI is fully responsive and works on all devices including phones and tablets."],
            ].map(([q, a]) => (
              <div key={q} style={{ background: "#0d1b3e", border: "1px solid #1a2a4a", borderRadius: "14px", padding: "20px 24px" }}>
                <div style={{ color: "#fff", fontWeight: 600, fontSize: "15px", marginBottom: "8px" }}>❓ {q}</div>
                <div style={{ color: "#6a80a0", fontSize: "14px", lineHeight: "1.6" }}>{a}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <footer style={{ background: "#030810", borderTop: "1px solid #1a2a4a", marginTop: "80px" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "48px 24px 24px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "24px", marginBottom: "32px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{ width: "36px", height: "36px", background: "linear-gradient(135deg,#ff6b35,#e05520)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", color: "#fff", fontWeight: 800 }}>∑</div>
              <div style={{ fontWeight: 700, fontSize: "16px", color: "#fff" }}>MathAI Tutor</div>
            </div>
            <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
              {[["Home", "#home"], ["Solver", "#home"], ["Pricing", "#pricing"], ["Privacy", "#"], ["Terms", "#"]].map(([l, h]) => (
                <a key={l} href={h} style={{ color: "#6a80a0", textDecoration: "none", fontSize: "14px", transition: "color .2s" }}
                  onMouseEnter={e => e.target.style.color = "#ff6b35"}
                  onMouseLeave={e => e.target.style.color = "#6a80a0"}>{l}</a>
              ))}
            </div>
          </div>
          <div style={{ borderTop: "1px solid #1a2a4a", paddingTop: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
            <div style={{ color: "#4a5a7a", fontSize: "13px" }}>© {new Date().getFullYear()} MathAI Tutor. All rights reserved.</div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#4a5a7a", fontSize: "12px" }}>
              <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#10b981", display: "inline-block" }}></span>
              All systems operational
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ===== AUTH MODAL =====
function AuthModal({ mode, onClose }) {
  const { login } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isLogin = mode === "login";

  const submit = async () => {
    setLoading(true); setError("");
    const action = isLogin ? "login" : "register";
    const body = isLogin ? { email: form.email, password: form.password } : form;
    const res = await fetch(`${API_BASE}/auth.php?action=${action}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const d = await res.json();
    setLoading(false);
    if (d.error) { setError(d.error); return; }
    login(d.token, d.user);
    onClose();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }} onClick={onClose}>
      <div style={{ background: "#0d1b3e", border: "1px solid #2a3a64", borderRadius: "20px", padding: "40px", maxWidth: "400px", width: "100%", position: "relative" }} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} style={{ position: "absolute", top: "16px", right: "16px", background: "none", border: "none", color: "#6a80a0", fontSize: "20px", cursor: "pointer" }}>✕</button>
        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <div style={{ fontSize: "32px", marginBottom: "8px" }}>🧮</div>
          <h2 style={{ color: "#fff", margin: 0, fontSize: "22px" }}>{isLogin ? "Welcome Back" : "Create Account"}</h2>
          <p style={{ color: "#6a80a0", fontSize: "14px", marginTop: "4px" }}>{isLogin ? "Sign in to your account" : "Start solving math problems"}</p>
        </div>
        {error && <div style={{ background: "rgba(255,60,60,.1)", border: "1px solid rgba(255,60,60,.3)", borderRadius: "8px", padding: "10px 14px", color: "#ff8080", fontSize: "13px", marginBottom: "16px" }}>{error}</div>}
        {!isLogin && (
          <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Full Name"
            style={{ width: "100%", padding: "12px 16px", background: "#0a1628", border: "1px solid #2a3a64", borderRadius: "10px", color: "#e0e8ff", fontSize: "14px", marginBottom: "12px", outline: "none", boxSizing: "border-box" }} />
        )}
        <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="Email Address"
          style={{ width: "100%", padding: "12px 16px", background: "#0a1628", border: "1px solid #2a3a64", borderRadius: "10px", color: "#e0e8ff", fontSize: "14px", marginBottom: "12px", outline: "none", boxSizing: "border-box" }} />
        <input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="Password"
          onKeyDown={e => e.key === "Enter" && submit()}
          style={{ width: "100%", padding: "12px 16px", background: "#0a1628", border: "1px solid #2a3a64", borderRadius: "10px", color: "#e0e8ff", fontSize: "14px", marginBottom: "20px", outline: "none", boxSizing: "border-box" }} />
        <button onClick={submit} disabled={loading}
          style={{ width: "100%", padding: "13px", background: "linear-gradient(135deg,#ff6b35,#e05520)", border: "none", borderRadius: "10px", color: "#fff", fontWeight: 700, fontSize: "15px", cursor: loading ? "not-allowed" : "pointer" }}>
          {loading ? "Please wait..." : isLogin ? "Sign In" : "Create Account"}
        </button>
        <p style={{ textAlign: "center", color: "#6a80a0", fontSize: "13px", marginTop: "16px" }}>
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <a href={`#${isLogin ? "register" : "login"}`} style={{ color: "#ff6b35", textDecoration: "none" }} onClick={onClose}>
            {isLogin ? "Sign up" : "Sign in"}
          </a>
        </p>
      </div>
    </div>
  );
}

// ===== MAIN APP =====
export default function App() {
  const [page, setPage] = useState("home");
  const [authModal, setAuthModal] = useState(null);

  useEffect(() => {
    const handleHash = () => {
      const hash = window.location.hash.replace("#", "") || "home";
      if (hash === "login") { setAuthModal("login"); setPage("home"); }
      else if (hash === "register") { setAuthModal("register"); setPage("home"); }
      else { setPage(hash); setAuthModal(null); }
    };
    handleHash();
    window.addEventListener("hashchange", handleHash);
    return () => window.removeEventListener("hashchange", handleHash);
  }, []);

  return (
    <AuthProvider>
      {authModal && <AuthModal mode={authModal} onClose={() => { setAuthModal(null); window.location.hash = "home"; }} />}
      {page === "pricing" ? <PricingPage /> : <SolverPage />}
    </AuthProvider>
  );
}
